<template>
  <v-dialog :value="sectionEditor.show" :width="1200" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <h3 class="label-input">Section Name</h3>
          <v-text-field v-model="fields.title" label="Name" />
        </div>

        <div class="mb-6">
          <label class="label-input mb-4 d-flex align-center gap-2" htmlFor="reviewShow" style="width: fit-content">
            <span>Customer Review</span>
            <input class="ml-2 cursor-pointer" type="checkbox" v-model="fields['review'].show" id="reviewShow" />
          </label>
          <v-select v-model="fields.review.position" :items="positionOptions" label="Position" />
          <div class="mb-4">
            <p>Reviewer Avatar</p>
            <UploadImage
              ref="reviewImageUpload"
              @callback="handleUploadReviewImage"
              :image="{ imageSrc: fields.review.image }"
              :height="`150`"
              :typeUpload="'image'"
              @callbackDelete="handleRemoveReviewImage"
            />
          </div>
          <div class="mb-6">
            <div class="mb-2">Review Text</div>
            <ckeditor :editor="editor" v-model="fields.review.text" :config="editorConfig" />
          </div>
          <v-text-field v-model="fields.review.name" label="Reviewer Name" />
        </div>

        <div class="mb-6">
          <h3 class="label-input">Main Content</h3>
          <div class="d-flex align-center gap-2 my-4">
            <div class="d-flex align-center">
              <input type="checkbox" v-model="fields.ratingShow" id="ratingShow" />
              <label class="ml-2" for="ratingShow">Star Rating</label>
            </div>
            <v-rating
              color="warning"
              background-color="#c1c1c1"
              empty-icon="mdi-star-outline"
              full-icon="mdi-star"
              half-icon="mdi-star-half-full"
              hover
              length="5"
              size="24"
              v-model="fields.rating"
            ></v-rating>
          </div>

          <div>
            <div class="mb-2">Content</div>
            <ckeditor :editor="editor" v-model="fields.content" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3 class="label-input mb-0">Feature List ({{ fields.list.length }})</h3>
            <v-btn small color="primary" @click="handleAddListItem">
              <v-icon size="16" left>mdi-plus</v-icon>
              Add Feature
            </v-btn>
          </div>

          <div v-if="fields.list.length === 0" class="text-center pa-4 grey--text">
            No features added yet. Click "Add Feature" to get started.
          </div>

          <v-expansion-panels v-else accordion>
            <v-expansion-panel v-for="(item, i) in fields.list" :key="i">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center" style="width: 100%">
                  <div class="d-flex align-center">
                    <div class="feature-thumbnail mr-3">
                      <img v-if="item.image" :src="item.image" alt="" />
                      <v-icon v-else color="grey lighten-1">mdi-image</v-icon>
                    </div>
                    <span v-html="item.text.replace(/<[^>]*>/g, '').trim()"></span>
                  </div>
                  <v-btn icon small @click.stop="handleRemoveListItem(i)">
                    <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="pa-2">
                  <div class="mb-4">
                    <UploadImage
                      :ref="`listImageUpload${i}`"
                      @callback="obj => handleUploadListImage(obj, i)"
                      :image="{ imageSrc: item.image }"
                      :height="`100`"
                      :typeUpload="'image'"
                      @callbackDelete="() => handleRemoveListImage(i)"
                    />
                  </div>

                  <ckeditor :editor="editor" v-model="item.text" :config="editorConfig" />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div class="mb-6">
          <h3 class="label-input">Footer</h3>
          <ckeditor :editor="editor" v-model="fields.footer" :config="editorConfig" />
        </div>

        <div class="mt-6">
          <v-row>
            <v-col cols="6">
              <v-checkbox v-model="fields.topPadding" label="Top Padding" />
            </v-col>
            <v-col cols="6">
              <v-checkbox v-model="fields.bottomPadding" label="Bottom Padding" />
            </v-col>
          </v-row>
        </div>

        <div class="mt-6">
          <label class="label-input">Colors</label>
          <v-row>
            <v-col md="6" cols="12">
              <div class="d-flex align-center gap-3">
                <input
                  type="color"
                  name="backgroundColor"
                  style="width: 40px; height: 40px; margin-right: 12px"
                  :value="fields.colors.backgroundColor"
                  @change="handleChangeBackgroundColor"
                />
                <v-text-field v-model="fields.colors.backgroundColor" label="Background Color" />
                <v-btn @click="handleResetBackgroundColor" small text color="primary"> Reset </v-btn>
              </div>
            </v-col>
            <v-col md="6" cols="12">
              <div class="d-flex align-center gap-3">
                <input
                  type="color"
                  name="backgroundColorContent"
                  style="width: 40px; height: 40px; margin-right: 12px"
                  :value="fields.colors.backgroundColorContent"
                  @change="handleChangeBackgroundColorContent"
                />
                <v-text-field v-model="fields.colors.backgroundColorContent" label="Background Color Content" />
                <v-btn @click="handleResetBackgroundColorContent" small text color="primary"> Reset </v-btn>
              </div>
            </v-col>
            <v-col md="6" cols="12">
              <div class="d-flex align-center gap-3">
                <input
                  type="color"
                  name="cardBackgroundColor"
                  style="width: 40px; height: 40px; margin-right: 12px"
                  :value="fields.colors.cardBackgroundColor"
                  @change="handleChangeCardBackgroundColor"
                />
                <v-text-field v-model="fields.colors.cardBackgroundColor" label="Card Background Color" />
                <v-btn @click="handleResetCardBackgroundColor" small text color="primary"> Reset </v-btn>
              </div>
            </v-col>
            <v-col md="6" cols="12">
              <div class="d-flex align-center gap-3">
                <input
                  type="color"
                  name="cardTextColor"
                  style="width: 40px; height: 40px; margin-right: 12px"
                  :value="fields.colors.cardTextColor"
                  @change="handleChangeCardTextColor"
                />
                <v-text-field v-model="fields.colors.cardTextColor" label="Card Text Color" />
                <v-btn @click="handleResetCardTextColor" small text color="primary"> Reset </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save Changes</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeepObject } from '../common/utils';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { replaceImageUrl } from '../common/utils';

export default {
  name: 'cardReviewSectionEditor',
  components: {
    UploadImage,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      positionOptions: [
        {
          text: 'Left',
          value: 'left',
        },
        {
          text: 'Right',
          value: 'right',
        },
      ],
      fields: {
        title: 'Card Review Section',
        enableAnchor: false,
        review: {
          image: '',
          text: '',
          name: '',
          position: 'left',
          show: true,
        },
        rating: 5,
        ratingShow: true,
        content: '',
        list: [],
        footer: '',
        colors: {
          backgroundColor: '',
          backgroundColorContent: '#EAFDD7',
          cardBackgroundColor: '#FFFFFF',
          cardTextColor: '#000000',
        },
        topPadding: true,
        bottomPadding: true,
      },
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },

    handleSave() {
      const updatedSection = {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtmlFromData.sectionCardReview(this.sectionEditor.section.html, this.fields),
      };
      this.$emit('update:section', updatedSection);
      this.handleClosePopup();
    },
    // Review image handlers
    handleUploadReviewImage(obj) {
      if (obj && obj.imageSrc) {
        this.fields.review.image = obj.imageSrc;
      }
    },

    handleRemoveReviewImage() {
      this.fields.review.image = '';
    },

    // List item handlers
    handleAddListItem() {
      this.fields.list.push({
        image: replaceImageUrl(
          'https://cdn.skyfastimg.com/images/2025/12/15/image-693f73bec6360038d84885d2.avif?w=50&h=50&q=80',
        ),
        text: 'Feature Text',
      });
    },

    handleRemoveListItem(index) {
      this.fields.list.splice(index, 1);
    },

    handleUploadListImage(obj, index) {
      if (obj && obj.imageSrc) {
        this.fields.list[index].image = obj.imageSrc;
      }
    },

    handleRemoveListImage(index) {
      this.fields.list[index].image = '';
    },

    handleChangeBackgroundColor(event) {
      this.fields.colors.backgroundColor = event.target.value;
    },
    handleResetBackgroundColor() {
      this.fields.colors.backgroundColor = '';
    },
    handleChangeBackgroundColorContent(event) {
      this.fields.colors.backgroundColorContent = event.target.value;
    },
    handleResetBackgroundColorContent() {
      this.fields.colors.backgroundColorContent = '#EAFDD7';
    },
    handleChangeCardBackgroundColor(event) {
      this.fields.colors.cardBackgroundColor = event.target.value;
    },
    handleResetCardBackgroundColor() {
      this.fields.colors.cardBackgroundColor = '#FFFFFF';
    },
    handleChangeCardTextColor(event) {
      this.fields.colors.cardTextColor = event.target.value;
    },
    handleResetCardTextColor() {
      this.fields.colors.cardTextColor = '#000000';
    },
  },

  mounted() {
    if (this.sectionEditor.sectionExtract) {
      this.fields = cloneDeepObject(this.sectionEditor.sectionExtract);
    }
  },

  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal.sectionExtract) {
          this.fields = cloneDeepObject(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ck-editor__editable_inline {
  min-height: 150px !important;
}
::v-deep .ck-editor__editable {
  min-height: 150px !important;
}

.label-input {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: block;
}

.header-modal,
.footer-modal {
  padding: 0 24px;
  border-bottom: 1px solid #e0e0e0;
}

.footer-modal {
  border-top: 1px solid #e0e0e0;
  border-bottom: none;
}

.d-flex {
  display: flex;
}

.align-center {
  align-items: center;
}

.gap-3 {
  gap: 12px;
}

.feature-thumbnail {
  width: 50px;
  height: 50px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

::v-deep .v-text-field--outlined {
  .v-input__control {
    .v-input__slot {
      min-height: 40px;
    }
  }
}

::v-deep .v-textarea {
  &.v-text-field--outlined {
    .v-input__control {
      .v-input__slot {
        min-height: 80px;
      }
    }
  }
}
</style>
